"use client";

import * as React from "react";

import { cn } from "lib/utils";
import { MapPin } from "lucide-react";

import type { AddressComponents, AddressFormData } from "@/types/address";
import { normalizeStateName } from "@/utils/normalizeAddress";

import { AddressAutocomplete } from "js/components/ui/address-autocomplete";
import { Alert, AlertActions, AlertDescription } from "js/components/ui/alert";
import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";

import { mexicoStates } from "../../../utils/constants";

export interface AddressFormProps {
  /**
   * Current address form data
   */
  value: AddressFormData;
  /**
   * Callback when any field changes
   */
  onChange: (data: AddressFormData) => void;
  /**
   * Callback when a field loses focus
   */
  onBlur?: (field: keyof AddressFormData) => void;
  /**
   * Field validation errors
   */
  errors?: Partial<Record<keyof AddressFormData, string>>;
  /**
   * Fields that have been touched
   */
  touched?: Partial<Record<keyof AddressFormData, boolean>>;
  /**
   * Whether the form is disabled
   */
  disabled?: boolean;
  /**
   * Whether data is loading
   */
  isLoading?: boolean;
  /**
   * Section title
   */
  title?: string;
  /**
   * Show section header
   */
  showHeader?: boolean;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Field configuration for character limits
   */
  fieldLimits?: Partial<Record<keyof AddressFormData, number>>;
  /**
   * Transform function for field values (e.g., uppercase)
   */
  transformValue?: (value: string) => string;
}

const DEFAULT_FIELD_LIMITS: Partial<Record<keyof AddressFormData, number>> = {
  street: 80,
  exterior_number: 10,
  interior_number: 10,
  neighborhood: 80,
  city: 80,
  municipality: 80,
  state: 40,
  zip_code: 5,
};

/**
 * AddressForm Component
 *
 * Unified address entry form with:
 * - Google Places autocomplete (when enabled via feature flag)
 * - Manual entry fallback
 * - Hybrid mode: autocomplete + manual editing
 *
 * @example
 * <AddressForm
 *   value={addressData}
 *   onChange={setAddressData}
 *   errors={formErrors}
 *   touched={touchedFields}
 * />
 */
export function AddressForm({
  value,
  onChange,
  onBlur,
  errors = {},
  touched = {},
  disabled = false,
  isLoading = false,
  title = "Domicilio",
  showHeader = true,
  className,
  fieldLimits = {},
  transformValue = (v) =>
    v
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\./g, ""),
}: AddressFormProps) {
  const [manualMode, setManualMode] = React.useState(false);
  const [manualEntryInitiated, setManualEntryInitiated] = React.useState(false);

  const limits = { ...DEFAULT_FIELD_LIMITS, ...fieldLimits };

  // Update field helper
  const updateField = <K extends keyof AddressFormData>(
    field: K,
    fieldValue: AddressFormData[K],
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  // Handle Google Places address selection
  const handleAddressSelect = (address: AddressComponents) => {
    const hasGoogleMunicipality = !!address.municipality;
    const finalMunicipality = hasGoogleMunicipality
      ? transformValue(address.municipality)
      : address.city
        ? transformValue(address.city)
        : value.municipality;

    onChange({
      street: transformValue(address.street),
      exterior_number: transformValue(address.exterior_number),
      interior_number: transformValue(address.interior_number || ""),
      neighborhood: address.neighborhood
        ? transformValue(address.neighborhood)
        : value.neighborhood,
      city: address.city ? transformValue(address.city) : value.city,
      municipality: finalMunicipality,
      state: address.state ? normalizeStateName(address.state) : value.state,
      zip_code: address.zip_code || value.zip_code,
    });
    setManualMode(true);
  };

  // Switch to manual mode
  const handleManualMode = () => {
    setManualEntryInitiated(true);
    setManualMode(true);
  };

  // Check if any address field has content
  const hasAddressContent = React.useMemo(() => {
    return (
      value.street.trim() !== "" ||
      value.exterior_number.trim() !== "" ||
      value.interior_number.trim() !== "" ||
      value.neighborhood.trim() !== "" ||
      value.city.trim() !== "" ||
      value.municipality.trim() !== "" ||
      value.state.trim() !== "" ||
      value.zip_code.trim() !== ""
    );
  }, [value]);

  // Fields are locked only when not in manual mode AND no content exists
  const isFieldLocked = !manualMode && !hasAddressContent;
  const showManualAlert = manualEntryInitiated && manualMode;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Section Header */}
      {showHeader && (
        <div className="flex items-center gap-2 pb-2">
          <MapPin className="h-4 w-4 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
      )}

      {/* Google Places Autocomplete (hidden only when manual mode was explicitly initiated) */}
      {!manualEntryInitiated && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">
            Buscar dirección
          </Label>
          <AddressAutocomplete
            onAddressSelect={handleAddressSelect}
            onManualModeClick={handleManualMode}
            disabled={disabled || isLoading}
            placeholder="Escribe una dirección..."
            showManualOption={true}
            manualOptionLabel="Ingresar manualmente"
          />
        </div>
      )}

      {/* Manual Mode Alert (only when user explicitly clicked "Ingresar manualmente") */}
      {showManualAlert && (
        <Alert variant="info" className="mb-4">
          <AlertDescription>
            Modo manual activado. Ingresa la dirección completa en los campos
            correspondientes.
          </AlertDescription>
          <AlertActions>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setManualMode(false);
                setManualEntryInitiated(false);
              }}
            >
              Volver a búsqueda
            </Button>
          </AlertActions>
        </Alert>
      )}

      {/* Street, Exterior Number, Interior Number */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Calle */}
        <div className="space-y-2">
          <Label htmlFor="street" className="text-sm font-medium text-gray-700">
            Calle *
          </Label>
          <Input
            id="street"
            type="text"
            value={value.street}
            onChange={(e) =>
              updateField("street", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("street")}
            maxLength={limits.street}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Calle"
            autoComplete="street-address"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.street && errors.street && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.street.length}/{limits.street}
            </p>
            {touched.street && errors.street && (
              <p className="text-xs text-red-600">{errors.street}</p>
            )}
          </div>
        </div>

        {/* Número Exterior */}
        <div className="space-y-2">
          <Label
            htmlFor="exterior_number"
            className="text-sm font-medium text-gray-700"
          >
            Número Exterior *
          </Label>
          <Input
            id="exterior_number"
            type="text"
            value={value.exterior_number}
            onChange={(e) =>
              updateField("exterior_number", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("exterior_number")}
            maxLength={limits.exterior_number}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Número Exterior"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.exterior_number &&
                errors.exterior_number &&
                "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.exterior_number.length}/{limits.exterior_number}
            </p>
            {touched.exterior_number && errors.exterior_number && (
              <p className="text-xs text-red-600">{errors.exterior_number}</p>
            )}
          </div>
        </div>

        {/* Número Interior */}
        <div className="space-y-2">
          <Label
            htmlFor="interior_number"
            className="text-sm font-medium text-gray-700"
          >
            Número Interior (opcional)
          </Label>
          <Input
            id="interior_number"
            type="text"
            value={value.interior_number}
            onChange={(e) =>
              updateField("interior_number", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("interior_number")}
            maxLength={limits.interior_number}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Número Interior"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.interior_number &&
                errors.interior_number &&
                "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.interior_number.length}/{limits.interior_number}
            </p>
            {touched.interior_number && errors.interior_number && (
              <p className="text-xs text-red-600">{errors.interior_number}</p>
            )}
          </div>
        </div>
      </div>

      {/* ZIP Code, Colonia, City, Municipality, State */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        {/* Código Postal */}
        <div className="space-y-2">
          <Label
            htmlFor="zip_code"
            className="text-sm font-medium text-gray-700"
          >
            Código Postal *
          </Label>
          <Input
            id="zip_code"
            type="text"
            value={value.zip_code}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/[^0-9]/g, "");
              updateField("zip_code", numericValue);
            }}
            onBlur={() => onBlur?.("zip_code")}
            maxLength={limits.zip_code}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Código Postal"
            autoComplete="postal-code"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.zip_code && errors.zip_code && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.zip_code.length}/{limits.zip_code}
            </p>
            {touched.zip_code && errors.zip_code && (
              <p className="text-xs text-red-600">{errors.zip_code}</p>
            )}
          </div>
        </div>

        {/* Colonia */}
        <div className="space-y-2">
          <Label
            htmlFor="neighborhood"
            className="text-sm font-medium text-gray-700"
          >
            Colonia *
          </Label>
          <Input
            id="neighborhood"
            type="text"
            value={value.neighborhood}
            onChange={(e) =>
              updateField("neighborhood", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("neighborhood")}
            maxLength={limits.neighborhood}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Colonia"
            autoComplete="address-level3"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.neighborhood && errors.neighborhood && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.neighborhood.length}/{limits.neighborhood}
            </p>
            {touched.neighborhood && errors.neighborhood && (
              <p className="text-xs text-red-600">{errors.neighborhood}</p>
            )}
          </div>
        </div>

        {/* Ciudad */}
        <div className="space-y-2">
          <Label htmlFor="city" className="text-sm font-medium text-gray-700">
            Ciudad *
          </Label>
          <Input
            id="city"
            type="text"
            value={value.city}
            onChange={(e) =>
              updateField("city", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("city")}
            maxLength={limits.city}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Ciudad"
            autoComplete="address-level2"
            className={cn(
              "border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
              touched.city && errors.city && "border-red-500",
            )}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {value.city.length}/{limits.city}
            </p>
            {touched.city && errors.city && (
              <p className="text-xs text-red-600">{errors.city}</p>
            )}
          </div>
        </div>

        {/* Municipio */}
        <div className="space-y-2">
          <Label
            htmlFor="municipality"
            className="text-sm font-medium text-gray-700"
          >
            Municipio *
          </Label>
          <Input
            id="municipality"
            type="text"
            value={value.municipality}
            onChange={(e) =>
              updateField("municipality", transformValue(e.target.value))
            }
            onBlur={() => onBlur?.("municipality")}
            disabled={isFieldLocked || disabled || isLoading}
            placeholder="Municipio"
            autoComplete="address-level1"
            className={cn(
              "border-gray-200",
              isFieldLocked &&
                "disabled:cursor-not-allowed disabled:opacity-50",
              touched.municipality && errors.municipality && "border-red-500",
            )}
          />
          {touched.municipality && errors.municipality && (
            <p className="text-xs text-red-600">{errors.municipality}</p>
          )}
        </div>

        {/* Estado */}
        <div className="space-y-2">
          <Label htmlFor="state" className="text-sm font-medium text-gray-700">
            Estado *
          </Label>
          <Select
            value={value.state}
            onValueChange={(newValue) => updateField("state", newValue)}
            disabled={isFieldLocked || disabled || isLoading}
          >
            <SelectTrigger
              aria-label="Estado"
              className={cn(
                "border-gray-200",
                isFieldLocked &&
                  "disabled:cursor-not-allowed disabled:opacity-50",
                touched.state && errors.state && "border-red-500",
              )}
            >
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(mexicoStates).map(([stateName, stateCode]) => (
                <SelectItem key={stateCode} value={stateName}>
                  {stateName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {touched.state && errors.state && (
            <p className="text-xs text-red-600">{errors.state}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddressForm;
